import Big from "big.js";
import { useState, useLayoutEffect, useCallback, useMemo, useRef } from "react";
import { Modal, useModalContext } from "../common/Modal";
import { TextInput } from "../common/TextInput";
import { gql, useMutation, useSuspenseQuery } from "@apollo/client";
import { AddOrEditFulfillmentModal_CreateOrderFulfillmentMutation, AddOrEditFulfillmentModal_CreateOrderFulfillmentMutationVariables, AddOrEditFulfillmentModal_UpdateOrderFulfillmentMutation, AddOrEditFulfillmentModal_UpdateOrderFulfillmentMutationVariables, AddOrEditFulfillmentModal_GetOrderQuery, AddOrEditFulfillmentModal_GetOrderQueryVariables, AddOrEditFulfillmentModal_GetFulfillmentQuery, AddOrEditFulfillmentModal_GetFulfillmentQueryVariables, CreateOrderFulfillmentInput, UpdateOrderFulfillmentInput, WithOrderItemInput, FullfillmentType } from "../../../codegen/graphql";
import { ButtonGroup } from "../common/ButtonGroup";
import { Select } from "../common/Select";
import { useRequestManager } from "../common/RequestManager";
import { getShippingServicesForPackage } from "../../../common/shipping";
import { sendcloud_packAndCarry_ups } from "../../../common/shipping/FR/sendcloud_packAndCarry_UPS";
import { DimensionsAndWeightPopover } from "../common/DimensionsPopover";
import { formatCurrency } from "../../utils/currency";
import { SupplierName } from "../../../common/suppliersConfig";
import Button from "../common/Button";
import { Toggle } from "../common/Toggle";
import { getTranslatedValue } from "../common/TranslatedValue";

// RegisterFulfillmentModal component for creating or editing fulfillments
interface AddOrEditFulfillmentModalProps {
  orderId: string;
  fulfillmentId?: string; // If provided, we're editing an existing fulfillment
  onClose: () => void;
  onSuccess: () => void;
}

const AddOrEditFulfillmentModal: React.FC<AddOrEditFulfillmentModalProps> = ({ 
  orderId, 
  fulfillmentId,
  onClose, 
  onSuccess,
}) => {
  const isEditing = !!fulfillmentId;
  
  return (
    <Modal
      onClose={onClose}
      title={isEditing ? "Edit Fulfillment" : "Add Fulfillment"}
      width="large"
    >
      <AddOrEditFulfillmentModalContent
        orderId={orderId}
        fulfillmentId={fulfillmentId}
        onClose={onClose}
        onSuccess={onSuccess}
      />
    </Modal>
  );
};

const AddOrEditFulfillmentModalContent: React.FC<AddOrEditFulfillmentModalProps> = ({ 
  orderId, 
  fulfillmentId,
  onClose, 
  onSuccess,
}) => {
  const modalContext = useModalContext();
  const { trackRequest } = useRequestManager();
  const isEditing = !!fulfillmentId;
  const estimatedCostInputRef = useRef<HTMLInputElement>(null);
  const estimatedCostVatInputRef = useRef<HTMLInputElement>(null);

  // Mutations
  const [addOrderFulfillment, { loading: addLoading }] = useMutation<
    AddOrEditFulfillmentModal_CreateOrderFulfillmentMutation,
    AddOrEditFulfillmentModal_CreateOrderFulfillmentMutationVariables
  >(CREATE_ORDER_FULFILLMENT_MUTATION);
  const [updateOrderFulfillment, { loading: updateLoading }] = useMutation<
    AddOrEditFulfillmentModal_UpdateOrderFulfillmentMutation,
    AddOrEditFulfillmentModal_UpdateOrderFulfillmentMutationVariables
  >(UPDATE_ORDER_FULFILLMENT_MUTATION);

  // Fetch order items for fulfillment creation
  const { data: orderDataQuery } = useSuspenseQuery<
    AddOrEditFulfillmentModal_GetOrderQuery,
    AddOrEditFulfillmentModal_GetOrderQueryVariables
  >(GET_ORDER_QUERY, {
    variables: { orderId: orderId },
    fetchPolicy: "network-only",
  });

  // Fetch existing fulfillment data if editing
  const shouldSkipFulfillmentQuery = !isEditing;
  const { data: fulfillmentDataQuery } = useSuspenseQuery<
    AddOrEditFulfillmentModal_GetFulfillmentQuery,
    AddOrEditFulfillmentModal_GetFulfillmentQueryVariables
  >(GET_FULFILLMENT_QUERY, {
    variables: { id: fulfillmentId! },
    skip: shouldSkipFulfillmentQuery,
    fetchPolicy: "network-only"
  });

  const orderData = orderDataQuery.order;
  if (!orderData) {
    throw new Error('Order not found');
  }

  const orderItemsData = orderData.orderProductsVariations;
  const fulfillmentData = fulfillmentDataQuery?.orderFulfillment;

  if (!fulfillmentData && !shouldSkipFulfillmentQuery) {
    throw new Error('Fulfillment not found');
  }

  const orderAfterSales = orderData.afterSales ?? [];

  // Form state
  const [form, setForm] = useState<(CreateOrderFulfillmentInput | UpdateOrderFulfillmentInput) & { forAfterSales?: string[] }>(() => {
    let withOrderItems: WithOrderItemInput[] = [];
    if (isEditing) {
      // Initialize form with existing fulfillment data
      withOrderItems = fulfillmentData!.orderProductsVariations.map((withOrderItem) => ({
        orderItemId: Number(withOrderItem.id),
        quantity: withOrderItem.quantity,
      }));

      const estimatedCost = fulfillmentData!.transactions.reduce((acc, transaction) => {
        return acc.plus(transaction.amount);
      }, new Big(0));
      const estimatedCostVat = fulfillmentData!.transactions.reduce((acc, transaction) => {
        return acc.plus(transaction.amountVat);
      }, new Big(0));
      const estimatedCostCurrency = fulfillmentData!.transactions[0]?.currency || 'EUR';

      const linkedAfterSaleIds = orderAfterSales
        .filter((a) => a.returnPackageId != null && String(a.returnPackageId) === String(fulfillmentId))
        .map((a) => a.id);

      return {
        type: fulfillmentData!.type,
        fullfilledOrderId: fulfillmentData!.fullfilledOrderId || '',
        fullfilledPlatform: fulfillmentData!.fullfilledPlatform || '',
        withOrderItems: withOrderItems,
        estimatedCost: estimatedCost.times(-1).toNumber(),
        estimatedCostVat: estimatedCostVat.times(-1).toNumber(),
        estimatedCostCurrency: estimatedCostCurrency,
        packaging: {
          weight: fulfillmentData!.packaging?.weight || 0,
          size1: fulfillmentData!.packaging?.size1 || 0,
          size2: fulfillmentData!.packaging?.size2 || 0,
          size3: 0,
        },
        forAfterSales: linkedAfterSaleIds,
        aliexpressIsRefunded: fulfillmentData!.aliexpressIsRefunded || false,
      }
    }
    else {
      // Initialize form with all order line items by default
      withOrderItems = orderItemsData.map((withOrderItem) => ({
        orderItemId: Number(withOrderItem.id),
        quantity: withOrderItem.quantity,
      }));

      return {
        ourOrderId: Number(orderId),
        type: FullfillmentType.Outward,
        fullfilledOrderId: '',
        fullfilledPlatform: '',
        withOrderItems: withOrderItems,
        estimatedCost: 0,
        estimatedCostVat: 0,
        estimatedCostCurrency: 'EUR',
        packaging: {
          weight: 0,
          size1: 0,
          size2: 0,
          size3: 0,
        },
        forAfterSales: [],
      }
    }
  });


  // Form event handlers
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    handleChange(name, value);
  }, []);

  const handleChange = useCallback((name: string, value: any) => {
    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  const handleLineItemSelection = useCallback((itemId: string) => {
    setForm(prev => {
      const isAlreadySelected = prev.withOrderItems.some(withOrderItem => withOrderItem.orderItemId === Number(itemId));
      if (isAlreadySelected) {
        return {
          ...prev,
          withOrderItems: prev.withOrderItems.filter(withOrderItem => (
            withOrderItem.orderItemId !== Number(itemId)
          ))
        };
      }
      else {
        const quantityInOrder = orderItemsData.find(item => item.id === itemId)!.quantity || 0;
        return {
          ...prev,
          withOrderItems: [...prev.withOrderItems, { orderItemId: Number(itemId), quantity: quantityInOrder }]
        };
      }
    });
  }, []);

  const handleLineItemQuantityChange = useCallback((itemId: string, quantity: number) => {
    setForm(prev => {
      const existingWithOrderItem = prev.withOrderItems.find(withOrderItem => withOrderItem.orderItemId === Number(itemId));
      if (!existingWithOrderItem) {
        return {
          ...prev,
          withOrderItems: [...prev.withOrderItems, { orderItemId: Number(itemId), quantity: quantity }]
        };
      }
      else {
        return {
          ...prev,
          withOrderItems: prev.withOrderItems.map(withOrderItem => (
            withOrderItem.orderItemId === Number(itemId)
              ? { ...withOrderItem, quantity: quantity }
              : withOrderItem
          ))
        };
      }
    });
  }, []);


  // Submitting the fulfillment
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (isEditing) {
      // Update existing fulfillment
      const updateInput: UpdateOrderFulfillmentInput = form as UpdateOrderFulfillmentInput;

      await trackRequest(
        updateOrderFulfillment({
          variables: {
            id: fulfillmentId!,
            input: updateInput
          }
        }),
        {
          title: 'Updating fulfillment',
          isBlocking: true,
        }
      );
    } else {
      // Create new fulfillment
      const createInput: CreateOrderFulfillmentInput = form as CreateOrderFulfillmentInput;

      await trackRequest(
        addOrderFulfillment({
          variables: {
            input: createInput
          }
        }),
        {
          title: 'Registering fulfillment',
          isBlocking: true,
        }
      );
    }

    onSuccess();
  }, [form, fulfillmentId, onSuccess]);

  // Set actions for the modal
  useLayoutEffect(() => {
    modalContext.setActions([
      {
        label: "Cancel",
        onClick: onClose,
        preset: "white"
      },
      {
        label: isEditing ? "Update" : "Register",
        onClick: handleSubmit,
        disabled: addLoading || updateLoading
      }
    ]);
  }, [onClose, handleSubmit, isEditing, addLoading, updateLoading, modalContext]);

  // Helper function to display variation details
  const getVariationDescription = (variation: any) => {
    if (!variation) return "Unknown variation";
    
    const variationAttributes = variation.variationTypeValues
      .map((tv: any) => `${getTranslatedValue(tv.type.type)}: ${getTranslatedValue(tv.value)}`)
      .join(", ");
      
    return `${getTranslatedValue(variation.ad.title)} (${variationAttributes})`;
  };

  const fulfillmentStatusOptions = [
    { value: 'PENDING_SHIPMENT', label: 'Pending Shipment' },
    { value: 'SHIPPING', label: 'Shipping' },
    { value: 'SHIPPING_OUTSIDE_NATIONAL', label: 'Shipping Outside National' },
    { value: 'SHIPPING_NATIONAL', label: 'Shipping National' },
    { value: 'SHIPPING_PENDING_PICKUP', label: 'Shipping Pending Pickup' },
    { value: 'DELIVERED', label: 'Delivered' },
    { value: 'SHIPPING_PROBLEM', label: 'Shipping Problem' },
  ];

  const platformOptions = [
    { identifier: '', label: 'Select Platform' },
    { identifier: 'aliexpress', label: 'AliExpress' },
    { identifier: 'sendcloud', label: 'SendCloud' },
    { identifier: 'leboncoin', label: 'LeBonCoin' },
    { identifier: 'ebay', label: 'eBay' },
    { identifier: 'temu', label: 'Temu' },
  ];

  const shippingCarrierOptions = [
    { value: '', label: 'Select Carrier' },
    { value: 'ups', label: 'UPS' },
    { value: 'dhl', label: 'DHL' },
    { value: 'fedex', label: 'FedEx' },
    { value: 'colissimo', label: 'Colissimo' },
    { value: 'chronopost', label: 'Chronopost' },
    { value: 'mondial_relay', label: 'Mondial Relay' },
  ];

  const currencyOptions = [
    { value: 'EUR', label: 'Euro' },
    { value: 'USD', label: 'US Dollar' },
    { value: 'GBP', label: 'British Pound' },
  ];

  const liveEstimatedCost = useMemo(() => {
    if (form.fullfilledPlatform === 'sendcloud') {
      const shippingServices = getShippingServicesForPackage({
        onlyServices: [sendcloud_packAndCarry_ups],
        packageWeight: form.packaging!.weight,
        packageSize1: form.packaging!.size1,
        packageSize2: form.packaging!.size2,
        packageSize3: form.packaging!.size3,
        orderPlatform: orderData.shop.shopType,
        origin: 'FR',
        destination: orderData.customerOriginalAddressCountryCode,
        supplier: SupplierName.FR_ilyes,
      });
      return shippingServices[0]?.rate;
    }
    return null;
  }, [form.fullfilledPlatform, form.packaging, orderData.shop.shopType, orderData.customerOriginalAddressCountryCode]);

  const toggleAfterSaleSelection = useCallback((afterSaleId: string) => {
    setForm((prev) => {
      const current = prev.forAfterSales ?? [];
      const isSelected = current.includes(afterSaleId);
      return {
        ...prev,
        forAfterSales: isSelected
          ? current.filter((id) => id !== afterSaleId)
          : [...current, afterSaleId],
      };
    });
  }, []);

  return (
    <div className="space-y-4">

      <ButtonGroup
        label="Type"
        name="type"
        options={[
          { value: FullfillmentType.Outward, label: 'Outward' },
          { value: FullfillmentType.Return, label: 'Return' },
        ]}
        value={form.type}
        onChange={(value) => handleChange('type', value)}
      />

      <ButtonGroup
        label="Platform"
        name="fullfilledPlatform"
        options={[
          // { value: '', label: 'No platform' },
          { value: 'direct-sale', label: 'Direct Sale' },
          { value: 'leboncoin', label: 'LeBonCoin' },
          { value: 'aliexpress', label: 'AliExpress' },
          { value: 'sendcloud', label: 'SendCloud' },
          { value: 'temu', label: 'Temu Shipping' },
          { value: 'ebay_packlink', label: 'eBay Packlink' },
        ]}
        value={form.fullfilledPlatform}
        onChange={(value) => handleChange('fullfilledPlatform', value)}
      />

      {form.type === FullfillmentType.Return && (
        <div className="mt-4">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Linked after sales</h3>
          <p className="text-sm text-gray-500 mb-4">
            Select which after sales are associated with this return fulfillment.
          </p>
          <div className="border rounded-md divide-y divide-gray-200 max-h-48 overflow-y-auto">
            {orderAfterSales.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">No after sales for this order</div>
            ) : (
              orderAfterSales.map((afterSale) => {
                const isSelected = (form.forAfterSales ?? []).includes(afterSale.id);
                return (
                  <div
                    key={afterSale.id}
                    className="p-4 flex items-center cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleAfterSaleSelection(afterSale.id)}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleAfterSaleSelection(afterSale.id)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-sm mr-3"
                    />
                    <div className="text-sm text-gray-700">
                      {afterSale.type} – {afterSale.status}
                      {afterSale.returnPackageId != null && String(afterSale.returnPackageId) !== String(fulfillmentId) && (
                        <span className="text-amber-600 ml-1">(linked to another fulfillment)</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <TextInput
        label="External Order ID"
        name="fullfilledOrderId"
        value={form.fullfilledOrderId || ''}
        onChange={handleInputChange}
        placeholder="External order ID"
      />

      <DimensionsAndWeightPopover
        dimensionsAndWeight={form.packaging!}
        onChange={(dimensionsAndWeight) => handleChange('packaging', dimensionsAndWeight)}
      />

      
      {/* Order Items Selection - Only for creation */}
      {orderItemsData && (
        <div className="mt-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Order Items</h3>
          <p className="text-sm text-gray-500 mb-4">
            Select which order items are included in this fulfillment.
          </p>
          
          <div className="border rounded-md divide-y divide-gray-200">
            {orderItemsData.map((item) => {
              const formLineItem = form.withOrderItems.find(withOrderItem => withOrderItem.orderItemId === Number(item.id));
              return (
                <div
                  key={item.id}
                  className="p-4 flex items-center cursor-pointer hover:bg-gray-100"
                  onClick={(e) => {
                    // When editing of the quantity, don't select the item
                    if (e.target instanceof HTMLInputElement) {
                      return;
                    }

                    handleLineItemSelection(item.id);
                  }}
                >
                  <input
                    type="checkbox"
                    id={`item-${item.id}`}
                    checked={formLineItem ? true : false}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded-sm mr-3"
                  />
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-700">
                      {getVariationDescription(item.adVariation)}
                    </div>
                    <div className="text-sm text-gray-500">
                      Quantity: {item.quantity}
                    </div>
                    {item.orderFulfillments && item.orderFulfillments.length > 0 && (
                      <div className="text-xs text-amber-600 mt-1">
                        Already included in {item.orderFulfillments.length} fulfillment(s)
                      </div>
                    )}
                  </div>
                  <div>
                    <TextInput
                      type="number"
                      value={formLineItem?.quantity || 0}
                      onChange={(e) => {
                        handleLineItemQuantityChange(item.id, parseInt(e.target.value));
                      }}
                      max={item.quantity}
                    />
                  </div>
                </div>
              )
            })}
            
            {(!orderItemsData || orderItemsData.length === 0) && (
              <div className="text-center py-4 text-gray-500">No order items found</div>
            )}
          </div>
        </div>
      )}

      {liveEstimatedCost && (
        <div className="flex flex-row gap-2">
          <div className="text-base text-gray-500">Live estimated cost: {formatCurrency(liveEstimatedCost.priceWithVAT)}</div>
          <div className="text-base text-gray-500">Live estimated cost VAT: {formatCurrency(new Big(liveEstimatedCost.priceWithVAT).minus(liveEstimatedCost.priceWithoutVAT).toNumber())}</div>
          <Button
            label="Fill"
            onClick={() => {
              estimatedCostInputRef.current!.value = liveEstimatedCost.priceWithVAT.toString();
              estimatedCostVatInputRef.current!.value = new Big(liveEstimatedCost.priceWithVAT).minus(liveEstimatedCost.priceWithoutVAT).toNumber().toString();
              handleChange('estimatedCost', parseFloat(estimatedCostInputRef.current!.value));
              handleChange('estimatedCostVat', parseFloat(estimatedCostVatInputRef.current!.value));
            }}
            size="sm"
          />
        </div>
      )}

      <div className="flex flex-row gap-2">
        <TextInput
          ref={estimatedCostInputRef}
          label="Estimated cost"
          name="estimatedCost"
          defaultValue={form.estimatedCost || '0'}
          onBlur={(e) => handleChange('estimatedCost', parseFloat(e.target.value))}
          placeholder="Estimated cost"
        />
        <TextInput
          ref={estimatedCostVatInputRef}
          label="Estimated cost VAT"
          name="estimatedCostVat"
          defaultValue={
            form.estimatedCostVat
            || '0'
          }
          onBlur={(e) => handleChange('estimatedCostVat', parseFloat(e.target.value))}
          placeholder="Estimated cost VAT"
        />
        <Select
          label="Estimated cost currency"
          name="estimatedCostCurrency"
          value={form.estimatedCostCurrency || ''}
          onChange={(value) => handleChange('estimatedCostCurrency', value)}
          options={currencyOptions}
        />
      </div>

      {form.fullfilledPlatform === 'aliexpress' && (
        <div className="flex flex-row gap-2">
          <Toggle
            label="AliExpress is refunded"
            value={form.aliexpressIsRefunded ?? false}
            onChange={(value) => handleChange('aliexpressIsRefunded', value)}
          />
        </div>
      )}
    </div>
  );
};

export default AddOrEditFulfillmentModal;


const ORDER_FULFILLMENT_FRAGMENT = gql`
  fragment AddOrEditFulfillmentModal_OrderFulfillment on OrderFulfillment {
    id
    type
    fullfilledOrderId
    fullfilledPlatform
    shippingCarrier
    trackingNumber
    fullfilledStatus
    shippedDate
    deliveredDate
    packaging {
      weight
      size1
      size2
      size3
    }
    orderProductsVariations {
      id
      quantity
      adVariation {
        id
        ad {
          title
        }
        variationTypeValues {
          type {
            type
          }
          value
        }
      }
    }
    transactions {
      id
      orderId
      lineItemId
      type
      key
      currency
      amount
      amountVat
    }
  }
`;

const CREATE_ORDER_FULFILLMENT_MUTATION = gql`
  mutation AddOrEditFulfillmentModal_CreateOrderFulfillment($input: CreateOrderFulfillmentInput!) {
    createOrderFulfillment(input: $input) {
      ...AddOrEditFulfillmentModal_OrderFulfillment
    }
  }
  ${ORDER_FULFILLMENT_FRAGMENT}
`;


const UPDATE_ORDER_FULFILLMENT_MUTATION = gql`
  mutation AddOrEditFulfillmentModal_UpdateOrderFulfillment($id: ID!, $input: UpdateOrderFulfillmentInput!) {
    updateOrderFulfillment(id: $id, input: $input) {
      ...AddOrEditFulfillmentModal_OrderFulfillment
    }
  }
  ${ORDER_FULFILLMENT_FRAGMENT}
`;


const GET_FULFILLMENT_QUERY = gql`
  query AddOrEditFulfillmentModal_GetFulfillment($id: ID!) {
    orderFulfillment(id: $id) {
      ...AddOrEditFulfillmentModal_OrderFulfillment
    }
  }
  ${ORDER_FULFILLMENT_FRAGMENT}
`;

const GET_ORDER_QUERY = gql`
  query AddOrEditFulfillmentModal_GetOrder($orderId: ID!) {
    order(id: $orderId) {
      shop {
        shopType
      }
      customerOriginalAddressCountryCode
      orderProductsVariations {
        id
        quantity
        adVariation {
          id
          ad {
            title
          }
          variationTypeValues {
            type {
              type
            }
            value
          }
        }
        orderFulfillments {
          id
          fullfilledPlatform
        }
      }
      afterSales {
        id
        type
        status
        returnPackageId
      }
    }
  }
`;